import {
  type AccountBalance,
  accountBalanceApi,
  type Token,
} from "../apis/account-balance";
import { type NetworkType } from "../models/app-config";
import { UserType, type UserInfoType } from "../models/profile/user-info";
import {
  formatIntegerNumber,
  getTopNetworks,
  getTopTokens,
  sumBalanceOfAccount,
} from "./common";
import { addressShorten } from "./wallet";

export interface BasicUserProfileInfo {
  profileName: string;
  profileBio: string;
  followerCount: number;
  followingCount: number;
  percentileScore: number;
  profileDescription: string;
  profileCoverUrl: string | null;
  isAvacusBot: boolean;
  isSystemAccount: boolean;
  nftAvatarImage: string;
  avatarImage: string;
  nftContractAddress: string;
  nftTokenId: string;
}

const getBasicUserProfileInfo = (
  profile: UserInfoType
): BasicUserProfileInfo => {
  const profileName =
    profile?.display_name ?? addressShorten(String(profile?.wallet_address));
  const profileBio = profile?.bio ?? "";
  const followerCount = formatIntegerNumber(profile?.follower_count ?? 0);
  const followingCount = formatIntegerNumber(profile?.following_count ?? 0);
  const percentileScore = profile?.percentile_score ?? 0;
  const profileDescription = profile?.description ?? "";
  const profileCoverUrl = profile?.cover?.url ?? null;
  const isSystemAccount = profile?.user_type === UserType.SYSTEM;
  const isAvacusBot = profile?.user_type === UserType.CHATBOT;
  const nftAvatarImage =
    profile?.nft_avatar?.animation_url ?? profile?.nft_avatar?.avatar_url ?? "";
  const avatarImage = profile?.avatar?.url ?? profile?.avatar?.avatar_url ?? "";
  const nftContractAddress = profile?.nft_avatar?.contract_address ?? "";
  const nftTokenId = profile?.nft_avatar?.token_id ?? "";

  return {
    profileName,
    profileBio,
    followerCount,
    followingCount,
    percentileScore,
    profileDescription,
    profileCoverUrl,
    isAvacusBot,
    isSystemAccount,
    nftAvatarImage,
    avatarImage,
    nftContractAddress,
    nftTokenId,
  };
};

const getInfoBalanceAndTokens = async (
  address: string
): Promise<{
  netWorth: string;
  topNetworks: NetworkType[];
  topTokens: Token[];
}> => {
  const res: any = await accountBalanceApi.getBalance(address);
  const accountBalance: AccountBalance = res.data;
  const netWorth = sumBalanceOfAccount(accountBalance.asset_value_usd);
  const topNetworks = getTopNetworks(accountBalance.asset_value_usd);
  const topTokens = getTopTokens(accountBalance.tokens);
  return { netWorth, topNetworks, topTokens };
};

export { getBasicUserProfileInfo, getInfoBalanceAndTokens };
